import trackEvent from '@ahd/shared/tools/track-event';
import { TrackEventOrigin } from '@ahd/shared/types/tracking/matomo/event-origin';
import { TrackEventSubType } from '@ahd/shared/types/tracking/matomo/event-sub-type';
import { TrackEventEventGenerateLeadEventSource } from '@ahd/shared/types/tracking/matomo/generate-lead/event-lead-source';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { format } from 'date-fns/format';
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { array as yupArray, boolean as yupBoolean, date as yupDate, number as yupNumber, object as yupObject, string as yupString } from 'yup';
import { DebugLevel } from '../../_configs/types';
import sIntMessageReasons from '../../_json/s_int_messageReasons';
import { type SendContactData, sendContact } from '../../_tools/axios/sendContact';
import { type Shape } from '../../_tools/utils';
import type { User } from '../../_types/Car';
import AnimationComponent from '../../components/animate/component';
import { type CalcFdProps, CalcFdType, type FinanceData } from '../../components/calculator/types';
import CarSelectDialog from '../../components/dialog/CarSelectDialog';
import ContactDialog from '../../components/dialog/ContactDialog';
import Iconify from '../../components/iconify/component';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { ConfigError } from '../../errors/ConfigError';
import formatText from '../../utils/texte/formatText';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import { LOCALSERVER } from '../../vars';
import { useBoolean } from '../use-boolean';
import useLocales from '../useLocales';
import { type ContactButtonConfigType, type ContactForm, type ContactTelephoneColor, type UseContactProps } from './types';

const BottomNavigation = lazy(() => import('@mui/material/BottomNavigation' /* webpackChunkName: "Mui_BottomNavigation" */));
const BottomNavigationAction = lazy(() => import('@mui/material/BottomNavigationAction' /* webpackChunkName: "Mui_BottomNavigationAction" */));
// DISABLED lazy import, otherwise flashing on hover over Fabs
// const Button = lazy(() => import('@mui/material/Button' /* webpackChunkName: "Mui_Button" */));
// const Fab = lazy(() => import('@mui/material/Fab' /* webpackChunkName: "Mui_Fab" */));
const Skeleton = lazy(() => import('@mui/material/Skeleton' /* webpackChunkName: "Mui_Skeleton" */));

let restorePrimaryColorTimer: NodeJS.Timeout;
let timerAutoUncleInit: NodeJS.Timeout;

export default function useContact(props: UseContactProps) {
    const { allFaarenVehicles, btns, car, loading, module } = props;
    const rootContainer = useRootContainerContext();
    const settings = useSettingsContext();
    const { translate } = useLocales();
    const [contactTelephoneColor, setContactTelephoneColor] = useState<ContactTelephoneColor>('primary');
    const [enableAutoUncleTradeIn, setEnableAutoUncleTradeIn] = useState<boolean>(false);
    const [hoverFabIndex, setHoverFabIndex] = useState<number>(undefined);
    const openCarSelector = useBoolean();
    const openContactForm = useBoolean();
    const theme = useTheme();
    const smRaw = useMediaQuery(theme?.breakpoints?.down('sm'));
    const sm = smRaw || !navigator.pdfViewerEnabled;
    const isSafari = navigator?.userAgent?.includes('Safari');

    const FormSchema = yupObject().shape<Shape<ContactForm>>({
        acceptDisclaimer: !checkUndefinedNullOrEmpty(settings?.contact?.useTextInsteadOfPrivacyCheckBox)
            ? undefined
            : yupBoolean().required(translate('common:validation.required')).oneOf([true], translate('common:carView.contact.errors.acceptDisclaimer')),
        accessoires: yupString().nullable(),
        accessoiresPrice: yupNumber().nullable(),
        callMeBack: yupBoolean().oneOf([false], ''),
        car: yupObject().shape<Shape<ContactForm['car']>>({
            id: yupNumber().required(translate('common:buttons.chooseVehicleHeading')),
        }),
        customerCreditInfo: yupObject().shape<Shape<ContactForm['customerCreditInfo']>>({
            gewerblicheAnfrage: yupBoolean().nullable(),
            unternehmen_alter: yupNumber().nullable(),
            insolvenzverfahren: yupBoolean().nullable(),
            unternehmer: yupBoolean().nullable(),
            fahrzeug_auch_gewerbliche_nutzung: yupBoolean().nullable(),
            monatliche_ausgaben: yupNumber().nullable(),
            monatliche_einkuenfte: yupNumber().nullable(),
            negativer_schufa_score: yupBoolean().nullable(),
            personen_in_haushalt: yupNumber().nullable(),
        }),
        email: yupString().required(translate('common:validation.required')).email(translate('common:validation.invalid')),
        event: yupNumber().required(translate('common:validation.required')).oneOf([0, 20], translate('common:validation.invalid')),
        message: yupString().required(translate('common:validation.required')),
        name: yupString().required(translate('common:validation.required')),
        tel: yupString().required(translate('common:validation.required')),
        termin: yupDate().when(['event', 'terminWithContactRequest'], ([event, terminWithContactRequest], schema) => {
            return event === 20 || (event === 0 && terminWithContactRequest === true) ? schema.required(translate('common:validation.required')) : schema.nullable();
        }),
        terminWithContactRequest: yupBoolean().nullable(),
        ansprechpartner: yupNumber().nullable(),
        fd: yupObject()
            .shape<Shape<ContactForm['fd']>>({
                annualPercentageRate: yupNumber().nullable(),
                damage_participation: yupNumber().nullable(),
                // @ts-expect-error type "" is not assignable to undefined
                delivery_option: yupArray().of(yupString()).nullable(),
                delivery_time_after_booking: yupNumber().nullable(),
                disclaimer: yupObject().shape<Shape<ContactForm['fd']['disclaimer']>>({
                    additional: yupString().nullable(),
                    info: yupString().nullable(),
                    summary: yupString().nullable(),
                }),
                downPayment: yupNumber().nullable(),
                duration: yupNumber().nullable(),
                extraMileageCosts: yupNumber().nullable(),
                fixed_duration: yupBoolean().nullable(),
                gapInsurance: yupString().nullable(),
                gapInsuranceAmount: yupNumber().nullable(),
                immediately_available: yupBoolean().nullable(),
                lastRate: yupNumber().nullable(),
                lowMileageCompensation: yupNumber().nullable(),
                mileage: yupNumber().nullable(),
                min_license_duration: yupNumber().nullable(),
                minimum_age: yupNumber().nullable(),
                monthlyRate: yupNumber().nullable(),
                monthlyRateNet: yupNumber().nullable(),
                netLoan: yupNumber().nullable(),
                nominalInterestRate: yupNumber().nullable(),
                paymentProtectionInsurance: yupString().nullable(),
                paymentProtectionInsuranceAmount: yupNumber().nullable(),
                // @ts-expect-error type "" is not assignable to undefined
                pick_up_option: yupArray().of(yupString()).nullable(),
                privateLeasing: yupBoolean().nullable(),
                total: yupNumber().nullable(),
                type: yupString().oneOf([CalcFdType.AutoAbo, CalcFdType.Financing, CalcFdType.Leasing]).nullable(),
            })
            .nullable(),
        afterSubmit: undefined,
    });

    const defaultValues: ContactForm = useMemo(() => {
        return {
            accessoires: '',
            accessoiresPrice: 0,
            car,
            event: 0,
            name: '',
            email: '',
            tel: '',
            termin: null,
            message: '',
            acceptDisclaimer: false,
            callMeBack: false,
            ansprechpartner: 0,
            fd: undefined,
            afterSubmit: undefined,
        };
    }, [car]);

    const methods = useForm<ContactForm>({
        // @ts-expect-error
        resolver: yupResolver<ContactForm>(FormSchema),
        defaultValues,
    });

    const { control, reset, setError, setValue } = methods;

    const onSubmit = useCallback(
        async (data: ContactForm) => {
            try {
                const mailToUse = LOCALSERVER || settings?.general?.debug === DebugLevel.CONTACT || settings?.general?.debug >= DebugLevel.INFO ? 'phillip@autohaus-digital.de' : data?.car?.loc?.hm;
                if (!checkUndefinedNullOrEmpty(mailToUse)) {
                    const eventElement = sIntMessageReasons(translate)?.find((m) => m?.i === data?.event);
                    const isXml = !checkUndefinedNullOrEmpty(settings?.contact?.blacklistLeadsystem)
                        ? !settings?.contact?.blacklistLeadsystem.includes(data?.car?.loc?.id)
                        : settings?.contact?.isLeadSystem;
                    const isCatchXml = isXml ? settings?.contact?.isCatchLeadSystem : false;
                    const { urlWithoutProtocol, ...catchCustomFields } = settings?.contact?.catchCustomFields || {};

                    const newData: SendContactData = {
                        dstMail: mailToUse,
                        xml: isXml,
                        catch_values: isCatchXml
                            ? {
                                ...catchCustomFields,
                                lead_type: data?.car?.typ,
                                marketing_url: urlWithoutProtocol ? `${location?.host}${location?.pathname}` : location?.href,
                                url: urlWithoutProtocol ? `${location?.host}${location?.pathname}` : location?.href,
                            }
                            : undefined,
                        isCatchXML: isCatchXml,
                        idCar: data?.car?.id,
                        idKontaktwunsh: data?.event,
                        name: data?.name,
                        email: data?.email,
                        telefon: data?.tel,
                        msg: data?.message,
                        idUser: !checkUndefinedOrNull(data?.ansprechpartner) ? data?.ansprechpartner : 0,
                        subjekt: isCatchXml
                            ? 'CATCH LEADS XML API'
                            : `${translate(`common:carView.contact.subjectPrefix${data?.fd?.type === CalcFdType.AutoAbo ? 'Abo' : ''}`)} ${data?.event === 20 ? `(${eventElement?.et}) ` : ''}Autohaus Digital Homepage-Tool`,
                        termin: data?.event === 20 || (data?.event === 0 && data?.terminWithContactRequest === true) ? format(data?.termin, 'yyyy-MM-dd HH:mm:ss') : undefined,
                        abo:
                            data?.fd?.type === CalcFdType.AutoAbo
                                ? {
                                    damage_participation: data?.fd?.damage_participation,
                                    delivery_option: data?.fd?.delivery_option,
                                    delivery_time_after_booking: data?.fd?.delivery_time_after_booking,
                                    duration: data?.fd?.duration,
                                    extraMileageCosts: data?.fd?.extraMileageCosts,
                                    fixed_duration: data?.fd?.fixed_duration,
                                    immediately_available: data?.fd?.immediately_available,
                                    mileage: data?.fd?.mileage,
                                    minimum_age: data?.fd?.minimum_age,
                                    min_license_duration: data?.fd?.min_license_duration,
                                    monthlyRate: data?.fd?.monthlyRate,
                                    monthlyRateNet: data?.fd?.monthlyRateNet,
                                    pick_up_option: data?.fd?.pick_up_option,
                                }
                                : undefined,
                        leasing:
                            data?.fd?.type === CalcFdType.Leasing
                                ? {
                                    anbieterbank: undefined,
                                    anzahlung: data?.fd?.downPayment,
                                    DestinationCharges: undefined,
                                    effektiver_jahreszins: (data?.fd?.annualPercentageRate || 0) * 100,
                                    ExtraMileageCosts: undefined,
                                    km: data?.fd?.mileage,
                                    laufzeit: data?.fd?.duration,
                                    LowMileageCompensation: undefined,
                                    monatliche_rate: data?.fd?.monthlyRate,
                                    monatliche_rate_netto: data?.fd?.monthlyRateNet,
                                    nettrokreditbetrag: data?.fd?.netLoan,
                                    RegistrationFees: undefined,
                                    soll_zinssatz: (data?.fd?.nominalInterestRate || 0) * 100,
                                    TotalAmount: data?.fd?.total,
                                    zielgruppe: data?.fd?.privateLeasing === true ? 'PRIVATE' : 'COMMERCIAL',
                                }
                                : undefined,
                        fin:
                            data?.fd?.type === CalcFdType.Financing
                                ? {
                                    abschlussgebühr: undefined,
                                    anbieterbank: undefined,
                                    anzahlung: data?.fd?.downPayment,
                                    art_sollzinssatzes: undefined,
                                    bruttokreditbetrag: Math.round(data?.fd?.total),
                                    effektiver_jahreszins: (data?.fd?.annualPercentageRate || 0) * 100,
                                    km: data?.fd?.mileage,
                                    ksb: data?.fd?.paymentProtectionInsurance,
                                    ksb_betrag: data?.fd?.paymentProtectionInsuranceAmount,
                                    laufzeit: data?.fd?.duration,
                                    monatliche_rate: Math.round(data?.fd?.monthlyRate),
                                    monatliche_rate_netto: Math.round(data?.fd?.monthlyRateNet),
                                    nettrokreditbetrag: Math.round(data?.fd?.netLoan),
                                    schlussrate: Math.round(data?.fd?.lastRate),
                                    soll_zinssatz: (data?.fd?.nominalInterestRate || 0) * 100,
                                    text1: undefined,
                                    text2: undefined,
                                }
                                : undefined,
                        customerCreditInfo: !checkUndefinedNullOrEmpty(data?.customerCreditInfo) ? data?.customerCreditInfo : undefined,
                        accessoires: data?.accessoires,
                    };

                    const sendResult = (await sendContact(data?.car?.idFirma, newData, settings?.general?.debug))?.data;
                    if (sendResult?.includes('Fehler')) {
                        setError('afterSubmit', { message: sendResult });
                    } else {
                        setValue('afterSubmit', translate('common:carView.contact.successSending'));

                        trackEvent(
                            TrackEventSubType.EventGenerateLead,
                            {
                                event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                                event_source: TrackEventEventGenerateLeadEventSource.Form,
                                event_source_object: `car_${data?.car?.id}`,
                                form_name: `Fahrzeugsuche - ${eventElement?.tGa}`,
                                value: data?.car?.preis?.vk,
                            },
                            settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
                        );

                        const scriptKey: 'formCommonSuccess' | 'formGeneralSuccess' | 'formTestDriveSuccess' | 'formFinancingSuccess' =
                            settings?.api?.tracking?.customScripts?.commonSuccess === true
                                ? !checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.formCommonSuccess)
                                    ? 'formCommonSuccess'
                                    : undefined
                                : !checkUndefinedNullOrEmpty(data?.fd?.type) && !checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.formFinancingSuccess)
                                    ? 'formFinancingSuccess'
                                    : data?.event === 20 && !checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.formTestDriveSuccess)
                                        ? 'formTestDriveSuccess'
                                        : !checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.formGeneralSuccess)
                                            ? 'formGeneralSuccess'
                                            : undefined;
                        if (!checkUndefinedNullOrEmpty(scriptKey)) {
                            try {
                                eval(settings?.api?.tracking?.customScripts?.[scriptKey]);
                            } catch (err) {
                                console.error('eval failed:', err);
                            }
                        }
                    }
                } else {
                    throw new ConfigError(translate, '002');
                }
            } catch (error) {
                console.error(error);
                setError('afterSubmit', error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setError, setValue, settings?.contact?.blacklistLeadsystem, settings?.contact?.isLeadSystem, settings?.contact?.isCatchLeadSystem, settings?.general?.idFirma]
    );

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    useEffect(() => {
        if (contactTelephoneColor !== 'primary') {
            clearTimeout(restorePrimaryColorTimer);
            restorePrimaryColorTimer = setTimeout(() => {
                setContactTelephoneColor('primary');
            }, 3000);
        }
    }, [contactTelephoneColor]);

    const values = useWatch({ control });
    const isProbefahrt = values?.event === 20;

    useEffect(
        function () {
            setEnableAutoUncleTradeIn(false);

            // Initial Timeout to make sure Script (re)-loads
            timerAutoUncleInit = setTimeout(function () {
                if (settings?.api?.autoUncle?.tradeIn === true) {
                    const check = !checkUndefinedOrNull(window?.auws?.openModal);
                    setEnableAutoUncleTradeIn(check);

                    if (check) {
                        clearTimeout(timerAutoUncleInit);
                        timerAutoUncleInit = setTimeout(function () {
                            window?.auWidgetEmbed?.init?.();
                            clearTimeout(timerAutoUncleInit);
                        }, 2000);
                    } else {
                        // Intervall checking if script loaded on details page, because React does not track global objects like window
                        const intervalId = setInterval(() => {
                            const isLoaded = !checkUndefinedOrNull(window?.auws?.openModal);
                            if (isLoaded) {
                                setEnableAutoUncleTradeIn(true);
                                clearInterval(intervalId);

                                clearTimeout(timerAutoUncleInit);
                                timerAutoUncleInit = setTimeout(() => {
                                    window?.auWidgetEmbed?.init?.();
                                    clearTimeout(timerAutoUncleInit);
                                }, 2000);
                            }
                        }, 500);

                        // Cleanup interval
                        return () => clearInterval(intervalId);
                    }
                }
                clearTimeout(timerAutoUncleInit);
            }, 2500);
        },
        [settings?.api?.autoUncle?.tradeIn]
    );

    const calculatorButton: CalcFdProps['calculatorButton'] = (fd: FinanceData, disabled: boolean) => (
        <Suspense fallback={<Skeleton sx={{ [getStyleQuery()]: { width: 370, height: 48 } }} />}>
            {loading === true ? (
                <Skeleton sx={{ [getStyleQuery()]: { width: 370, height: 48 } }} />
            ) : (
                <Button
                    color="primary"
                    data-testid="useContact_calculator_button"
                    disabled={disabled || fd?.valid !== true}
                    fullWidth
                    size="large"
                    variant="contained"
                    type="submit"
                    onClick={(e) => {
                        e?.preventDefault();
                        setValue('event', 0);
                        setValue('fd', fd);
                        openContactForm?.onTrue();
                    }}
                >
                    {formatText(
                        translate('common:carView.calculator.button'),
                        fd?.type === CalcFdType.Leasing && fd?.privateLeasing === false
                            ? fd?.monthlyRateNet?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : fd?.monthlyRate?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    )}
                    {values?.accessoiresPrice > 0 && (
                        <>
                            <br />
                            {translate('common:carView.accessoires.contactPlusAccessoiresPrice')}
                        </>
                    )}
                </Button>
            )}
        </Suspense>
    );

    const carSelectDialog = openCarSelector.value === true && (
        <CarSelectDialog allFaarenVehicles={allFaarenVehicles} defaultValues={defaultValues} methods={methods} module={module} openCarSelector={openCarSelector} values={values as ContactForm} />
    );

    const contactDialog = openContactForm?.value === true && (
        <ContactDialog
            carSelectDialog={carSelectDialog}
            defaultValues={defaultValues}
            isProbefahrt={isProbefahrt}
            loading={loading}
            methods={methods}
            module={module}
            onSubmit={onSubmit}
            openContactForm={openContactForm}
            openCarSelector={openCarSelector}
            rootContainer={rootContainer}
            values={values as ContactForm}
        />
    );

    const contactButtonConfig: ContactButtonConfigType = [
        // Phone
        !checkUndefinedNullOrEmpty(values?.car?.loc?.tel_hpt)
            ? {
                // i: 'tel', t: sm ? translate('common:carView.contactTab.call') : (contactTelephoneColor !== 'primary' ? translate(`common:carView.contact.copyTel.${contactTelephoneColor}`) : values?.car?.loc?.tel_hpt), color: contactTelephoneColor, href: `tel:${values?.car?.loc?.tel_hpt}`, icon: 'mdi:phone', textBtnFunc: () => {
                i: 'tel',
                t: translate('common:carView.contactTab.call'),
                color: contactTelephoneColor,
                href: `tel:${values?.car?.loc?.tel_hpt}`,
                icon: 'mdi:phone',
                textBtnFunc: () => {
                    try {
                        if (!sm) {
                            if (typeof navigator?.clipboard !== 'undefined') {
                                navigator?.clipboard?.writeText(values?.car?.loc?.tel_hpt);
                                setContactTelephoneColor('success');
                            } else {
                                setContactTelephoneColor('error');
                                throw new Error('Data cannot be copied to clipboard');
                            }
                        }
                    } catch (err) {
                        console.error(err);
                    }
                    trackEvent(
                        TrackEventSubType.EventGenerateLead,
                        {
                            event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                            event_source: TrackEventEventGenerateLeadEventSource.Phone,
                            event_source_object: `car_${values?.car?.id}`,
                            value: values?.car?.preis?.vk,
                        },
                        settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
                    );

                    if (!checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.phoneClick)) {
                        try {
                            eval(settings?.api?.tracking?.customScripts?.phoneClick);
                        } catch (err) {
                            console.error('eval failed:', err);
                        }
                    }
                },
                func: () => {
                    trackEvent(
                        TrackEventSubType.EventGenerateLead,
                        {
                            event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                            event_source: TrackEventEventGenerateLeadEventSource.Phone,
                            event_source_object: `car_${values?.car?.id}`,
                            value: values?.car?.preis?.vk,
                        },
                        settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
                    );

                    if (!checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.phoneClick)) {
                        try {
                            eval(settings?.api?.tracking?.customScripts?.phoneClick);
                        } catch (err) {
                            console.error('eval failed:', err);
                        }
                    }
                },
                hidden: btns?.phone === false,
            }
            : undefined,
        // Trade-In
        {
            i: '27',
            t: translate('common:carView.contact.requestTypes.27Short'),
            color: 'primary' as ContactTelephoneColor,
            // Disable if AutoUncle tradeIn but script not loaded yet on detail page
            disabled: settings?.api?.autoUncle?.tradeIn === true && enableAutoUncleTradeIn === false,
            href: undefined,
            icon: 'mdi:hand-coin',
            func: () => {
                if (enableAutoUncleTradeIn) { // if (enableAutoUncleTradeIn && !checkUndefinedNullOrEmpty(values?.car?.id)) {
                    // Embedded AutoUncle TradeIn Modal (in detail page only)
                    window?.auws?.openModal(window?.auws?.ModuleTypes?.TRADE_IN);
                } else {
                    // Link to page with TradeIn
                    window.open(settings?.fahrzeugsuche?.tradeInLink, '_blank');
                }

                trackEvent(
                    TrackEventSubType.EventGenerateLead,
                    {
                        event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                        event_source: TrackEventEventGenerateLeadEventSource.TradeIn,
                        event_source_object: `car_${values?.car?.id}`,
                        value: values?.car?.preis?.vk,
                    },
                    settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
                );

                if (!checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.tradeInClick)) {
                    try {
                        eval(settings?.api?.tracking?.customScripts?.tradeInClick);
                    } catch (err) {
                        console.error('eval failed:', err);
                    }
                }
            },
            hidden: (
                btns?.tradeIn === false || (!settings?.api?.autoUncle?.tradeIn && checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.tradeInLink))
                // || (enableAutoUncleTradeIn === false && checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.tradeInLink))
                // // Disable if AutoAbo tradeIn but not on detail page for modal and no external tradeInLink defined
                // || (settings?.api?.autoUncle?.tradeIn === true
                //     && checkUndefinedNullOrEmpty(settings?.fahrzeugsuche?.tradeInLink)
                //     && checkUndefinedNullOrEmpty(values?.car?.loc?.id)
                // )
            ),
        },
        // AutoUncle Price Alert
        settings?.api?.autoUncle.priceAlert === true && settings?.general?.debug === DebugLevel.EXTERNALS // TODO remove debug check
            ? {
                i: 'priceAlert',
                t: translate('common:carView.contact.priceAlert'),
                color: 'primary' as ContactTelephoneColor,
                href: undefined,
                icon: 'mdi:alarm',
                func: () => {
                    if (enableAutoUncleTradeIn && !checkUndefinedNullOrEmpty(values?.car?.id)) {
                        window?.auws?.openModal(window?.auws?.ModuleTypes?.PRICE_ALERT);
                    }

                    trackEvent(
                        TrackEventSubType.EventGenerateLead,
                        {
                            event_origin: settings?.general?.isLandingpage ? TrackEventOrigin.LANDINGPAGE : TrackEventOrigin.HPT,
                            event_source: TrackEventEventGenerateLeadEventSource.PriceAlert,
                            event_source_object: `car_${values?.car?.id}`,
                            value: values?.car?.preis?.vk,
                        },
                        settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO
                    );

                    if (!checkUndefinedNullOrEmpty(settings?.api?.tracking?.customScripts?.priceAlertClick)) {
                        try {
                            eval(settings?.api?.tracking?.customScripts?.priceAlertClick);
                        } catch (err) {
                            console.error('eval failed:', err);
                        }
                    }
                },
                hidden: btns?.priceAlert === false || checkUndefinedNullOrEmpty(values?.car?.id),
            }
            : undefined,
        // Test Drive
        !settings?.fahrzeugsuche?.hideProbefahrt
            ? {
                i: '20',
                t: translate('common:carView.contact.requestTypes.20Short'),
                color: 'primary' as ContactTelephoneColor,
                href: undefined,
                icon: 'mdi:steering',
                func: () => {
                    setValue('event', 20);
                    openContactForm?.onTrue();
                },
                hidden: btns?.testDrive === false,
            }
            : undefined,
        // Contact Request
        {
            i: '0',
            t: translate('common:carView.contact.requestTypes.0Short'),
            color: 'primary' as ContactTelephoneColor,
            href: undefined,
            icon: 'mdi:email',
            func: () => {
                setValue('event', 0);
                openContactForm?.onTrue();
            },
            hidden: btns?.message === false,
        },
    ].filter((val) => !checkUndefinedNullOrEmpty(val));

    // CTA Button placement values
    const position = settings?.contact?.buttons?.position || 'right';
    const offsetX = settings?.contact?.buttons?.offsetX || 2;
    const offsetY = settings?.contact?.buttons?.offsetY || 2;

    const contactButtons = sm ? (
        <Suspense fallback={<div />}>
            <BottomNavigation color="primary.main" showLabels sx={{ [getStyleQuery()]: { position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.appBar } }}>
                {contactButtonConfig
                    ?.filter((btn) => !btn?.hidden && btn?.i !== 'priceAlert')
                    ?.map((btn) => (
                        <BottomNavigationAction
                            aria-label={btn?.t}
                            key={`useContact_BottomNavigationBtn_${btn.i}`}
                            // @ts-expect-error
                            component={!checkUndefinedNullOrEmpty(btn?.href) ? 'a' : undefined}
                            disabled={settings?.general?.mockup}
                            data-testid={`UseContact_ContactButton_${btn?.i}`}
                            href={btn?.href}
                            label={btn?.t}
                            icon={<Iconify width="1.3rem" icon={btn?.icon} />}
                            onClick={btn?.func}
                            target={!checkUndefinedNullOrEmpty(btn?.href) && isSafari ? '_blank' : undefined}
                        />
                    ))}
            </BottomNavigation>
        </Suspense>
    ) : (
        <Stack
            alignItems={settings?.contact?.buttons?.position === 'right' ? 'flex-end' : 'flex-start'}
            gap={1}
            sx={{
                [getStyleQuery()]: {
                    position: 'fixed',
                    [position]: (theme) => theme.spacing(offsetX),
                    bottom: (theme) => theme.spacing(offsetY),
                    zIndex: (theme) => theme.zIndex.speedDial,
                },
            }}
        >
            {contactButtonConfig
                ?.filter((btn) => !btn?.hidden)
                ?.map((btn, i) => (
                    <Stack
                        key={`useContact_ContactButton_${btn.i}`}
                        alignItems="center"
                        direction={position === 'right' ? 'row-reverse' : 'row'}
                        gap={2}
                        onMouseEnter={() => setHoverFabIndex(i)}
                        onMouseLeave={() => setHoverFabIndex(undefined)}
                    >
                        <Suspense
                            fallback={
                                <>
                                    <Skeleton variant="circular" sx={{ height: 40, width: 40 }} />
                                    <Skeleton variant="rectangular" sx={{ borderRadius: 2, height: 24, width: [150, 100, 75][i] }} />
                                </>
                            }
                        >
                            <Fab
                                aria-label={btn?.t}
                                color={btn?.color}
                                component={!checkUndefinedNullOrEmpty(btn?.href) ? 'a' : undefined}
                                data-testid={`UseContact_ContactButton_${btn?.i}`}
                                disabled={btn?.disabled}
                                href={btn?.href}
                                onClick={btn?.func}
                                target={!checkUndefinedNullOrEmpty(btn?.href) && isSafari ? '_blank' : undefined}
                                variant="circular"
                            >
                                <Iconify width="1.35rem" icon={btn?.icon} />
                            </Fab>
                            {/* Display Buttons on hover over Fab */}
                            {hoverFabIndex === i &&
                                <AnimationComponent type="fade" direction={position === 'right' ? '-left' : '-right'} distance={0.1} duration={500} module={module}>
                                    <Button
                                        color={btn?.color}
                                        data-testid={`UseContact_ContactTextButton_${btn?.i}`}
                                        disabled={btn?.disabled}
                                        size="small"
                                        variant="soft"
                                        onClick={btn?.textBtnFunc || btn?.func}
                                    >
                                        {btn?.t === translate('common:carView.contact.requestTypes.0Short') && values?.accessoiresPrice > 0
                                            ? `${btn?.t} ${translate('common:carView.accessoires.contactPlusAccessoiresPrice')}`
                                            : btn?.t}
                                    </Button>
                                </AnimationComponent>
                            }
                        </Suspense>
                    </Stack>
                ))}
        </Stack>
    );

    // Contact request button below car data grid, Share & Expose button if financing & leasing are disabled
    const contactButtonLarge = (
        <Suspense fallback={<Skeleton sx={{ [getStyleQuery()]: { width: 370, height: 48 } }} />}>
            <Button
                data-testid={'UseContact_ContactTextButton_ContactLarge'}
                color="primary"
                onClick={() => {
                    setValue('event', 0);
                    openContactForm?.onTrue();
                }}
                size="large"
                variant="contained"
            >
                {values?.accessoiresPrice > 0
                    ? `${translate('common:carView.contact.requestTypes.0Short')} ${translate('common:carView.accessoires.contactPlusAccessoiresPrice')}`
                    : translate('common:carView.contact.requestTypes.0Short')}
            </Button>
        </Suspense>
    );

    const locationUserButton = (userId: User['i'], disabled: boolean) => (
        <Suspense fallback={<div />}>
            {loading === true ? (
                <Skeleton sx={{ [getStyleQuery()]: { width: 370, height: 48 } }} />
            ) : (
                <Button
                    id={`useContact_locationUserButton_${userId}`}
                    color="primary"
                    disabled={disabled || checkUndefinedNullOrEmpty(userId)}
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ [getStyleQuery()]: { borderRadius: 0 } }}
                    onClick={() => {
                        setValue('ansprechpartner', userId);
                        setValue('event', 0);
                        openContactForm?.onTrue();
                    }}
                >
                    {translate('common:carView.locationUsersContact')}
                </Button>
            )}
        </Suspense>
    );

    return {
        calculatorButton,
        contactButtons,
        contactButtonLarge,
        contactDialog,
        locationUserButton,
        setValue,
    };
}
